import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LinkIcon from '@mui/icons-material/Link';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useQueryClient } from '@tanstack/react-query';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { LandingSiteFormDtoIn } from '@/models/in/LandingSiteFormDtoIn';
import FormDetailsDialog from './FormDetailsDialog';
import ReplaceDocumentDialog from './ReplaceDocumentDialog';
import EditLinkDialog from './EditLinkDialog';
import EditDetailsDialog from './EditDetailsDialog';
import { LANDING_SITE_FORMS_URL, summarizeForTile } from './landingSiteForms';
import styles from './LandingSiteFormsPage.module.scss';

/**
 * The three cards of the landing site's "forms and approvals" section, in the order the visitor
 * meets them in. The first two share a row and approvals takes the one below it, which is also how
 * they sit on the site.
 */
const SECTIONS = [
  { key: 'funds', Icon: AccountBalanceIcon, isFullWidth: false },
  { key: 'donationForms', Icon: DescriptionIcon, isFullWidth: false },
  { key: 'approvals', Icon: VerifiedUserIcon, isFullWidth: true },
];

/**
 * Manages the documents, addresses and particulars behind the landing site's "forms and approvals"
 * section. The list is fixed - it is the section as the visitor sees it - so every entry is a tile,
 * and opening one shows what is behind it and everything that was there before it.
 */
const LandingSiteFormsPage = () => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const { data: forms, isLoading } = useGetAuthApi<LandingSiteFormDtoIn[]>(LANDING_SITE_FORMS_URL);

  // Which entries the dialogs are on, rather than copies of them: the publishing dialog sits over
  // the details one, and when it is done both have to be looking at the entry as it now stands.
  const [openedKey, setOpenedKey] = useState<string | null>(null);
  const [changingKey, setChangingKey] = useState<string | null>(null);

  const entry = (key: string | null) => (forms ?? []).find((form) => form.key === key) ?? null;
  const openedForm = entry(openedKey);
  const changingForm = entry(changingKey);

  /**
   * Everything this screen reads about the entries: the list behind the tiles, and the history
   * behind whichever entry is open. A published version changes both, and they are separate queries.
   */
  const reloadEntries = () => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith(LANDING_SITE_FORMS_URL),
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <h1 className="heading-h2-bold">{t('landingSiteForms.title')}</h1>
        <p className={`body-sm-regular ${styles.subtitle}`}>{t('landingSiteForms.subtitle')}</p>
      </div>

      {isLoading ? (
        <p className="body-sm-regular">{t('loading')}</p>
      ) : (
        <div className={styles.sections}>
          {SECTIONS.map(({ key, Icon, isFullWidth }) => {
            const sectionForms = (forms ?? []).filter((form) => form.section === key);
            if (sectionForms.length === 0) return null;

            return (
              <section key={key} className={isFullWidth ? styles.fullWidthCard : styles.card}>
                <header className={styles.cardHeader}>
                  <span className={styles.cardHeaderIcon}>
                    <Icon fontSize="small" />
                  </span>
                  <h2 className={`body-md-semibold ${styles.cardTitle}`}>
                    {t(`landingSiteForms.sections.${key}`)}
                  </h2>
                </header>

                <div className={styles.tiles} style={tileColumns(sectionForms.length)}>
                  {sectionForms.map((form) => (
                    <button
                      key={form.key}
                      type="button"
                      className={styles.tile}
                      onClick={() => setOpenedKey(form.key)}
                    >
                      <span className={styles.tileIcon}>
                        <TileIcon kind={form.kind} />
                      </span>
                      <span className={`body-md-semibold ${styles.tileLabel}`}>
                        {t(`landingSiteForms.items.${form.key}`)}
                      </span>
                      <span className={`body-sm-regular ${styles.tileState}`}>{summarizeForTile(form)}</span>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {openedForm && (
        <FormDetailsDialog
          form={openedForm}
          onClose={() => setOpenedKey(null)}
          onChange={() => setChangingKey(openedForm.key)}
          onRestored={reloadEntries}
        />
      )}

      {/* Over the details dialog rather than instead of it, and closing it leaves that one open. */}
      {changingForm && (
        <ChangeDialog form={changingForm} onClose={() => setChangingKey(null)} onSuccess={reloadEntries} />
      )}
    </div>
  );
};

/** What an entry is decides how it is changed: a file is uploaded, an address typed, values edited. */
const ChangeDialog = ({
  form,
  onClose,
  onSuccess,
}: {
  form: LandingSiteFormDtoIn;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  if (form.kind === 'File') return <ReplaceDocumentDialog form={form} onClose={onClose} onSuccess={onSuccess} />;
  if (form.kind === 'Details') return <EditDetailsDialog form={form} onClose={onClose} onSuccess={onSuccess} />;
  return <EditLinkDialog form={form} onClose={onClose} onSuccess={onSuccess} />;
};

const TileIcon = ({ kind }: { kind: LandingSiteFormDtoIn['kind'] }) => {
  if (kind === 'File') return <PictureAsPdfIcon />;
  if (kind === 'Details') return <ListAltIcon />;
  return <LinkIcon />;
};

/**
 * A section lays its entries out in a single row, so the number of columns is simply how many
 * entries it has. Capped so that a section that grows later widens rather than turning its tiles
 * into slivers.
 */
const tileColumns = (entryCount: number): CSSProperties =>
  ({ '--columns': Math.min(entryCount, 6) } as CSSProperties);

export default LandingSiteFormsPage;
